import { createContext, useContext } from 'react';
import type { Session, User } from '@supabase/supabase-js';

import type { Profile, TenantId } from '@/types';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly session: Session | null;
  readonly user: User | null;
  /**
   * Always the `tenant_id` claim of the current access token — see `readTenantId`. Null while
   * loading, signed out, or when the token carries no claim (a user not yet linked to a tenant).
   */
  readonly tenantId: TenantId | null;
  /**
   * Always the `profile` claim of the current access token — see `readProfile`. Null when the
   * claim is absent or unrecognised, which every consumer must read as "not authorised" rather
   * than as a default profile.
   */
  readonly profile: Profile | null;
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /**
   * Sends the "create a new password" e-mail. Resolves the same whether or not the address has
   * an account — Supabase answers alike for both, and so must every screen built on it.
   */
  readonly requestPasswordReset: (email: string, redirectTo: string) => Promise<void>;
  /** Signs in with the one-time token of a recovery e-mail, so the new password can be set. */
  readonly verifyRecovery: (tokenHash: string) => Promise<void>;
  readonly updatePassword: (password: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  }
  return context;
}
